import React from "react";
import { X, Check, Briefcase, Users, ArrowRight, TrendingUp, Layers, ShieldCheck, Sprout } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
export default function TheEsgDifference() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const dependencyPoints = [{
    text: "Delivers strategy to you",
    icon: Briefcase
  }, {
    text: "Leaves after the report",
    icon: X
  }, {
    text: "Creates dependency",
    icon: Layers
  }, {
    text: "Revenue-share models",
    icon: TrendingUp
  }, {
    text: "Generic higher ed frameworks",
    icon: Layers
  }];
  const capabilityPoints = [{
    text: "Builds strategy with you",
    icon: ShieldCheck
  }, {
    text: "Embeds until it works",
    icon: Users
  }, {
    text: "Transfers capability\xA0",
    icon: TrendingUp
  }, {
    text: "Fixed fee &\xA0 mission-aligned\n",
    icon: Check
  }, {
    text: "100% adult learner & lifelong learning specialization",
    icon: Check
  }];
  return <section id="the-esg-difference" className="dark bg-background text-foreground py-20 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={ref} className={cn("text-center mb-16 transition-all duration-700 transform", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold mb-6 tracking-tight">
            The ESG Difference
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
            Most firms rent you their expertise. We help you build your own.
            Compare the traditional consulting model with our capability-first approach.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 lg:h-[600px] max-w-6xl mx-auto">
          {}
          <Card className={cn("flex-1 border-none shadow-xl overflow-hidden transition-all duration-500 ease-in-out", "bg-[#F8F7F4] text-[#2D2D2D]", "lg:hover:flex-[0.8] lg:flex-1")}>
            <CardHeader className="pb-2 pt-10 px-8 md:px-12">
              <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center mb-6 text-gray-600">
                <Briefcase className="w-6 h-6" />
              </div>
              <CardTitle className="text-2xl md:text-3xl font-serif font-bold text-[#2D2D2D]">
                The Dependency Model
              </CardTitle>
              <CardDescription className="text-gray-600 text-base font-medium uppercase tracking-wider mt-2">
                Traditional Consulting
              </CardDescription>
            </CardHeader>
            <CardContent className="px-8 md:px-12 pb-8 pt-4 flex flex-col h-full">
              <p className="text-gray-600 mb-8 leading-relaxed">
                Traditional firms often operate as a "black box," delivering polished solutions that your team doesn't understand and can't maintain without hiring them back.
              </p>
              <ul className="space-y-4 mb-4">
                {dependencyPoints.map((item, i) => <li key={i} data-index={i} className="flex items-start gap-4 group">
                    <div className="mt-1 min-w-5 text-gray-400 group-hover:text-red-500 transition-colors">
                      <X className="w-5 h-5" />
                    </div>
                    <span className="text-gray-700 font-medium">{item.text}</span>
                  </li>)}
              </ul>
            </CardContent>
          </Card>

          {}
          <Card className={cn("flex-1 border-none shadow-2xl overflow-hidden transition-all duration-500 ease-in-out relative group", "bg-[#1B2A4A] text-white", "lg:flex-1 lg:hover:flex-[1.4]")}>
            {}
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#2A7F7F] opacity-10 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none" />
            
            <CardHeader className="pb-2 pt-10 px-8 md:px-12 relative z-10">
              <div className="w-12 h-12 rounded-full bg-[#E8A838]/20 flex items-center justify-center mb-6 text-[#E8A838]">
                <Sprout className="w-6 h-6" />
              </div>
              <CardTitle className="text-2xl md:text-3xl font-serif font-bold text-white">
                The Capability Model
              </CardTitle>
              <CardDescription className="text-[#E8A838] text-base font-bold uppercase tracking-wider mt-2">
                EduStrategy Group
              </CardDescription>
            </CardHeader>
            <CardContent className="px-8 md:px-12 pb-12 pt-8 flex flex-col h-full relative z-10">
              <p className="text-gray-300 mb-8 leading-relaxed">
                We co-design ecosystems with your team, embedding the skills, tools, and strategies you need to grow sustainably long after our engagement ends.
              </p>
              <ul className="space-y-5 mb-6">
                {capabilityPoints.map((item, i) => <li key={i} data-index={i} className="flex items-start gap-4">
                    <div className="mt-1 min-w-5 text-[#E8A838]">
                      <Check className="w-5 h-5" />
                    </div>
                    <span className="text-gray-100 font-medium">{item.text}</span>
                  </li>)}
              </ul>
              
              <div className="mt-auto pt-4 opacity-90 group-hover:opacity-100 transition-opacity">
                <Button asChild className="bg-[#E8A838] hover:bg-[#E8A838]/90 text-[#1B2A4A] font-bold tracking-wide" size="default">
                  <Link to="/how-we-work">
                    See How We Work <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}